import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, ShieldCheck, Lock, CreditCard } from 'lucide-react';
export function Hero() {
  const microBenefits = [{
    emoji: '📘',
    text: '6 tomos completos'
  }, {
    emoji: '🎁',
    text: '6 bonos incluidos'
  }, {
    emoji: '📄',
    text: '100% PDF'
  }, {
    emoji: '⚡',
    text: 'Acceso inmediato'
  }];
  return <section className="relative pt-6 pb-12 md:pt-14 md:pb-24 overflow-hidden bg-navy-900 border-b border-white/5" data-id="element-103">
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" data-id="element-104">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.5) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-105" />
      </div>

      {/* Soft gold radial glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[700px] bg-gold/[0.07] blur-[120px] rounded-full pointer-events-none" data-id="element-106"></div>

      {/* Top brand bar */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-5 md:mb-10" data-id="element-107">
        <div className="flex items-center justify-center sm:justify-between" data-id="element-108">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex items-center gap-2" data-id="element-109">
            <div className="w-7 h-7 rounded-full border border-gold/40 flex items-center justify-center text-gold text-xs" data-id="element-110">
              🩺
            </div>
            <span className="font-editorial text-gold text-sm sm:text-base tracking-wide" data-id="element-111">
              Atlas Clínico
            </span>
          </motion.div>
        </div>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center" data-id="element-112">
        {/* Trust line */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="inline-flex items-center gap-2 mb-4 sm:mb-6" data-id="element-113">
          <div className="flex gap-0.5" data-id="element-114">
            {[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-gold text-gold" data-id="element-115" />)}
          </div>
          <span className="text-xs sm:text-sm text-gray-300 font-medium" data-id="element-116">
            <span className="text-white font-bold" data-id="element-117">4.9/5</span> · +300
            estudiantes y kinesiólogos
          </span>
        </motion.div>

        {/* Eyebrow */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-[10px] sm:text-xs font-bold tracking-[0.12em] sm:tracking-[0.15em] uppercase mb-5 sm:mb-7 max-w-[300px] sm:max-w-md leading-tight" data-id="element-118">
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse flex-shrink-0" data-id="element-119"></span>
          <span data-id="element-120">Evaluación traumatológica y musculoesquelética</span>
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="font-heading font-extrabold text-white leading-[0.92] tracking-tight mb-5 sm:mb-7" data-id="element-121">
          <span className="block text-[2.75rem] sm:text-7xl md:text-8xl lg:text-[8rem]" data-id="element-122">
            MEGA PACK
          </span>
          <span className="block text-[2.25rem] sm:text-6xl md:text-7xl lg:text-[7rem] text-transparent bg-clip-text bg-gradient-to-r from-gold-light via-gold to-gold-light" data-id="element-123">
            KINESIOLOGÍA
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="text-base sm:text-xl md:text-2xl text-white font-medium mb-3 sm:mb-5 max-w-3xl leading-snug px-2" data-id="element-124">
          Aprendé a evaluar con criterio, usar los{' '}
          <span className="font-editorial italic text-gold font-normal" data-id="element-125">
            tests correctos
          </span>{' '}
          y dejar de improvisar frente al paciente.
        </motion.p>

        {/* Support paragraph */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="text-xs sm:text-base text-gray-400 mb-6 sm:mb-9 max-w-2xl leading-relaxed px-2" data-id="element-126">
          6 tomos + 6 bonos en PDF para estudiar, practicar y consultar
          anamnesis, pruebas ortopédicas, diagnóstico diferencial, red flags y
          razonamiento clínico{' '}
          <span className="font-editorial italic text-gold/90" data-id="element-127">
            en un solo sistema.
          </span>
        </motion.p>

        {/* Microbenefits */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1.5 mb-7 sm:mb-9 max-w-md sm:max-w-none" data-id="element-128">
          {microBenefits.map((benefit, idx) => <Fragment key={idx} data-id="element-129">
              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-white/90" data-id="element-130">
                <span data-id="element-131">{benefit.emoji}</span>
                <span className="font-medium" data-id="element-132">{benefit.text}</span>
              </div>
              {idx < microBenefits.length - 1 && <span className="hidden sm:inline text-gray-600" data-id="element-133">·</span>}
            </Fragment>)}
        </motion.div>

        {/* Primary CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="flex justify-center w-full mb-5 sm:mb-6" data-id="element-134">
          <a href="#pricing" className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy-950 font-bold text-sm sm:text-lg px-6 sm:px-10 py-4 sm:py-5 rounded-full transition-all transform hover:scale-[1.02] shadow-[0_0_50px_rgba(201,168,76,0.35)] w-full max-w-[340px] sm:max-w-none sm:w-auto" data-id="element-135">
            QUIERO MI MEGA PACK AHORA
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-136" />
          </a>
        </motion.div>

        {/* Trust/payment badges */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="flex flex-wrap justify-center items-center gap-x-3 sm:gap-x-4 gap-y-1.5 text-[11px] sm:text-sm text-gray-400" data-id="element-137">
          <div className="flex items-center gap-1.5" data-id="element-138">
            <ShieldCheck size={14} className="text-green-400 flex-shrink-0" data-id="element-139" />
            <span data-id="element-140">Garantía 7 días</span>
          </div>
          <span className="text-gray-600" data-id="element-141">·</span>
          <div className="flex items-center gap-1.5" data-id="element-142">
            <Lock size={14} className="text-green-400 flex-shrink-0" data-id="element-143" />
            <span data-id="element-144">Pago seguro</span>
          </div>
          <span className="text-gray-600" data-id="element-145">·</span>
          <div className="flex items-center gap-1.5" data-id="element-146">
            <CreditCard size={14} className="text-blue-400 flex-shrink-0" data-id="element-147" />
            <span data-id="element-148">Mercado Pago</span>
          </div>
        </motion.div>
      </div>
    </section>;
}